import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Area,
  BarChart,
  Bar,
  ReferenceLine,
  PieChart,
  Pie,
} from "recharts";

// Format the data for the cloud cover chart
const formatChartData = (dataArray) => {
  if (!dataArray) return [];

  return dataArray.map((item) => {
    // Create month-day label for x-axis
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const monthName = monthNames[item.month - 1];

    return {
      date: `${monthName} ${item.day}`,
      min: item.min,
      max: item.max,
      mean: item.mean,
      dayOfYear: item.day_of_year,
      month: item.month,
    };
  });
};

// Calculate some statistics for interpretation
const calculateStats = (chartData) => {
  if (!chartData || chartData.length === 0) return null;

  const annualMean =
    chartData.reduce((sum, item) => sum + item.mean, 0) / chartData.length;
  const maxValue = Math.max(...chartData.map((item) => item.max));
  const minValue = Math.min(...chartData.map((item) => item.min));

  // Find month with highest and lowest mean values
  const monthlyMeans = Array(12)
    .fill(0)
    .map((_, i) => {
      const monthData = chartData.filter((item) => item.month === i + 1);
      if (monthData.length === 0)
        return { month: i + 1, mean: 0, min: 0, max: 0 };

      return {
        month: i + 1,
        mean:
          monthData.reduce((sum, item) => sum + item.mean, 0) /
          monthData.length,
        min: Math.min(...monthData.map((item) => item.min)),
        max: Math.max(...monthData.map((item) => item.max)),
      };
    });

  const highestMonth = monthlyMeans.reduce((prev, current) =>
    prev.mean > current.mean ? prev : current
  );
  const lowestMonth = monthlyMeans.reduce((prev, current) =>
    prev.mean < current.mean ? prev : current
  );

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Calculate percentage of days in different cloud cover categories
  const clearDays = chartData.filter((day) => day.mean <= 30).length;
  const partlyCloudyDays = chartData.filter(
    (day) => day.mean > 30 && day.mean <= 70
  ).length;
  const cloudyDays = chartData.filter((day) => day.mean > 70).length;

  // Calculate seasonal patterns
  const seasons = [
    { name: "Winter", months: [12, 1, 2] },
    { name: "Spring", months: [3, 4, 5] },
    { name: "Summer", months: [6, 7, 8] },
    { name: "Fall", months: [9, 10, 11] },
  ];

  const seasonalStats = seasons.map((season) => {
    const seasonData = chartData.filter((item) =>
      season.months.includes(item.month)
    );
    if (seasonData.length === 0) return { name: season.name, mean: "0.0" };

    const seasonMean =
      seasonData.reduce((sum, item) => sum + item.mean, 0) / seasonData.length;
    return {
      name: season.name,
      mean: seasonMean.toFixed(1),
    };
  });

  return {
    annualMean: annualMean.toFixed(1),
    maxValue: maxValue.toFixed(1),
    minValue: minValue.toFixed(1),
    highestMonth: monthNames[highestMonth.month - 1],
    lowestMonth: monthNames[lowestMonth.month - 1],
    clearDaysPercent: ((clearDays / chartData.length) * 100).toFixed(1),
    partlyCloudyDaysPercent: (
      (partlyCloudyDays / chartData.length) *
      100
    ).toFixed(1),
    cloudyDaysPercent: ((cloudyDays / chartData.length) * 100).toFixed(1),
    monthlyMeans: monthlyMeans.map((m) => ({
      ...m,
      name: monthNames[m.month - 1].substring(0, 3),
      mean: m.mean.toFixed(1),
      min: m.min.toFixed(1),
      max: m.max.toFixed(1),
    })),
    seasonalStats,
  };
};

// Function to determine sky condition based on cloud cover percentage
// eslint-disable-next-line no-unused-vars
const getSkyCondition = (cloudCover) => {
  if (cloudCover <= 30) return "Clear";
  if (cloudCover <= 70) return "Partly Cloudy";
  return "Cloudy";
};

// Calculate solar radiation potential based on cloud cover
const calculateSolarPotential = (cloudCover) => {
  // Simple approximation: 100% - cloud cover percentage
  // This is a simplified model - actual solar potential would depend on many factors
  const cloudCoverValue = parseFloat(cloudCover);
  if (isNaN(cloudCoverValue)) return "0.0";
  return (100 - cloudCoverValue).toFixed(1);
};

// Get color based on cloud cover value
const getCloudCoverColor = (value) => {
  const numValue = parseFloat(value);
  if (numValue <= 30) return "#4caf50"; // Clear - Green
  if (numValue <= 70) return "#ff9800"; // Partly cloudy - Orange
  return "#f44336"; // Cloudy - Red
};

const SolarExposureCharts = ({ solarData, radiationData }) => {
  // Debug logs
  console.log("SolarExposureCharts received radiationData:", radiationData);
  console.log("SolarExposureCharts received solarData:", solarData);

  // Calculate monthly cloud cover data for bar chart - moved before conditional return
  const monthlyCloudCoverData = useMemo(() => {
    if (!solarData || !solarData.data || solarData.data.length === 0) return [];

    const stats = calculateStats(formatChartData(solarData.data));
    return stats?.monthlyMeans || [];
  }, [solarData]);

  // Calculate seasonal cloud cover data for bar chart - moved before conditional return
  // eslint-disable-next-line no-unused-vars
  const seasonalCloudCoverData = useMemo(() => {
    if (!solarData || !solarData.data || solarData.data.length === 0) return [];

    const stats = calculateStats(formatChartData(solarData.data));
    return stats?.seasonalStats || [];
  }, [solarData]);

  // Format solar radiation data for the charts
  const formatSolarRadiationData = (dataArray) => {
    if (!dataArray || !dataArray.length) {
      console.log("No radiation data available to format");
      return [];
    }

    console.log("Formatting radiation data:", dataArray);
    console.log("First few radiation data items:", dataArray.slice(0, 5));
    console.log(
      "Sample radiation values:",
      dataArray.slice(0, 5).map((item) => ({
        direct: item.direct_normal_rad,
        diffuse: item.diffuse_horizontal_rad,
        global: item.global_horizontal_rad,
        hour: item.hour,
      }))
    );

    // Check if we need to scale the values (if they're too small)
    const sampleValues = dataArray
      .slice(0, 100)
      .map(
        (item) =>
          item.global_horizontal_rad ||
          item.direct_normal_rad + item.diffuse_horizontal_rad
      )
      .filter((val) => val > 0);

    const maxSampleValue =
      sampleValues.length > 0 ? Math.max(...sampleValues) : 0;
    console.log(`Maximum sample radiation value: ${maxSampleValue}`);

    // If max value is very low, it might be in kWh/m² instead of Wh/m²
    const needsScaling = maxSampleValue > 0 && maxSampleValue < 10;
    const scaleFactor = needsScaling ? 1000 : 1;

    if (needsScaling) {
      console.log(
        `Values appear to be in kWh/m² - applying scaling factor of ${scaleFactor}`
      );
    }

    // Group data by month for monthly averages
    const monthlyData = Array(12)
      .fill()
      .map(() => []);

    dataArray.forEach((item) => {
      // Check for the API data format
      if (
        item.month &&
        (item.global_horizontal !== undefined ||
          item.diffuse_horizontal !== undefined)
      ) {
        monthlyData[item.month - 1].push({
          direct: (item.direct_normal || 0) * scaleFactor,
          diffuse: (item.diffuse_horizontal || 0) * scaleFactor,
          global: (item.global_horizontal || 0) * scaleFactor,
          hour: item.hour || 0,
        });
      }
      // Fallback for older data format
      else if (
        item.month &&
        item.direct_normal_rad !== undefined &&
        item.diffuse_horizontal_rad !== undefined
      ) {
        const direct = item.direct_normal_rad * scaleFactor;
        const diffuse = item.diffuse_horizontal_rad * scaleFactor;
        const global =
          item.global_horizontal_rad !== undefined
            ? item.global_horizontal_rad * scaleFactor
            : direct + diffuse;

        monthlyData[item.month - 1].push({
          direct,
          diffuse,
          global,
          hour: item.hour || 0,
        });
      }
    });

    // Log monthly data counts
    monthlyData.forEach((month, idx) => {
      console.log(`Month ${idx + 1} has ${month.length} radiation data points`);

      // Log some statistics for each month
      if (month.length > 0) {
        const directValues = month.map((item) => item.direct);
        const diffuseValues = month.map((item) => item.diffuse);
        const globalValues = month.map((item) => item.global);

        console.log(`Month ${idx + 1} stats:
          Direct: min=${Math.min(...directValues)}, max=${Math.max(
          ...directValues
        )}, avg=${directValues.reduce((a, b) => a + b, 0) / directValues.length}
          Diffuse: min=${Math.min(...diffuseValues)}, max=${Math.max(
          ...diffuseValues
        )}, avg=${
          diffuseValues.reduce((a, b) => a + b, 0) / diffuseValues.length
        }
          Global: min=${Math.min(...globalValues)}, max=${Math.max(
          ...globalValues
        )}, avg=${globalValues.reduce((a, b) => a + b, 0) / globalValues.length}
        `);
      }
    });

    // Calculate monthly averages by hour
    const monthlyAverages = monthlyData.map((month, index) => {
      // Group by hour
      const hourlyData = Array(24)
        .fill()
        .map(() => ({ direct: [], diffuse: [], global: [] }));

      month.forEach((item) => {
        const hour = item.hour;
        if (hour >= 0 && hour < 24) {
          hourlyData[hour].direct.push(item.direct);
          hourlyData[hour].diffuse.push(item.diffuse);
          if (item.global !== undefined) {
            hourlyData[hour].global.push(item.global);
          }
        }
      });

      // Calculate averages for each hour
      const hourlyAverages = hourlyData.map((hour, hourIndex) => {
        const directAvg = hour.direct.length
          ? hour.direct.reduce((sum, val) => sum + val, 0) / hour.direct.length
          : 0;
        const diffuseAvg = hour.diffuse.length
          ? hour.diffuse.reduce((sum, val) => sum + val, 0) /
            hour.diffuse.length
          : 0;
        const globalAvg = hour.global.length
          ? hour.global.reduce((sum, val) => sum + val, 0) / hour.global.length
          : directAvg + diffuseAvg; // If no global data, estimate as sum of direct and diffuse

        return {
          hour: hourIndex,
          direct: directAvg,
          diffuse: diffuseAvg,
          global: globalAvg,
          total: directAvg + diffuseAvg,
          month: index + 1,
        };
      });

      return {
        month: index + 1,
        monthName: [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ][index],
        hourlyData: hourlyAverages,
      };
    });

    return monthlyAverages;
  };

  // Calculate solar radiation statistics
  const calculateSolarStats = (monthlyData) => {
    if (!monthlyData || !monthlyData.length) return null;

    console.log("Calculating solar stats from monthly data:", monthlyData);

    // Calculate annual totals and averages
    let totalDirect = 0;
    let totalDiffuse = 0;
    // eslint-disable-next-line no-unused-vars
    let totalHours = 0;
    let peakDirect = 0;
    let peakDiffuse = 0;
    let peakMonth = "";

    // Count total data points for debugging
    let totalDataPoints = 0;
    monthlyData.forEach((month) => {
      totalDataPoints += month.hourlyData.length;
    });
    console.log(
      `Total hourly data points across all months: ${totalDataPoints}`
    );

    // Log a sample of hourly data to check values
    if (monthlyData[0] && monthlyData[0].hourlyData.length > 0) {
      console.log("Sample hourly data from January:");
      console.log(monthlyData[0].hourlyData.slice(0, 5));
    }

    // MACROCLIMATE VIEW CALCULATION
    // For macroclimate view, we need to calculate the average daily radiation for each month
    // and then calculate the annual average from those monthly averages
    const monthlyAverages = [];

    monthlyData.forEach((month) => {
      // Calculate total radiation for the month
      let monthlyDirectSum = 0;
      let monthlyDiffuseSum = 0;
      let nonZeroHours = 0;

      // Process each hour
      month.hourlyData.forEach((hour) => {
        if (hour.total > 0) {
          monthlyDirectSum += hour.direct;
          monthlyDiffuseSum += hour.diffuse;
          nonZeroHours++;

          if (hour.direct > peakDirect) {
            peakDirect = hour.direct;
            peakMonth = month.monthName;
          }

          if (hour.diffuse > peakDiffuse) {
            peakDiffuse = hour.diffuse;
          }
        }
      });

      // Calculate average daily radiation for the month
      // First, get the number of days in the month
      const daysInMonth = new Date(
        new Date().getFullYear(),
        month.month,
        0
      ).getDate();

      // Calculate daily average for the month (total radiation / days in month)
      // We need to scale by 24 because we're working with hourly data
      const dailyDirectAvg = (monthlyDirectSum * 24) / daysInMonth;
      const dailyDiffuseAvg = (monthlyDiffuseSum * 24) / daysInMonth;
      const dailyTotalAvg = dailyDirectAvg + dailyDiffuseAvg;

      console.log(
        `Month ${month.monthName}: Direct sum = ${monthlyDirectSum}, Diffuse sum = ${monthlyDiffuseSum}, ` +
          `Non-zero hours = ${nonZeroHours}/${month.hourlyData.length}, ` +
          `Days = ${daysInMonth}, Daily avg = ${dailyTotalAvg.toFixed(
            2
          )} Wh/m²/day`
      );

      // Store monthly average
      monthlyAverages.push({
        month: month.month,
        name: month.monthName,
        directAvg: dailyDirectAvg,
        diffuseAvg: dailyDiffuseAvg,
        totalAvg: dailyTotalAvg,
      });

      // Add to annual totals (we'll divide by 12 later to get annual average)
      totalDirect += dailyDirectAvg;
      totalDiffuse += dailyDiffuseAvg;
    });

    // Calculate annual average (average of monthly averages)
    const annualDirectAvg = totalDirect / monthlyData.length;
    const annualDiffuseAvg = totalDiffuse / monthlyData.length;
    const annualTotalAvg = annualDirectAvg + annualDiffuseAvg;

    console.log(
      `Annual Average Daily Radiation: ${annualTotalAvg.toFixed(
        2
      )} Wh/m²/day ` +
        `(Direct: ${annualDirectAvg.toFixed(
          2
        )}, Diffuse: ${annualDiffuseAvg.toFixed(2)})`
    );

    // Calculate monthly totals for the bar chart
    const monthlyTotals = monthlyAverages.map((month) => {
      // Calculate percentage of diffuse vs direct
      const diffusePercent =
        month.totalAvg > 0
          ? ((month.diffuseAvg / month.totalAvg) * 100).toFixed(1)
          : 0;
      const directPercent =
        month.totalAvg > 0
          ? ((month.directAvg / month.totalAvg) * 100).toFixed(1)
          : 0;

      return {
        month: month.month,
        name: month.name,
        direct: month.directAvg.toFixed(0),
        diffuse: month.diffuseAvg.toFixed(0),
        total: month.totalAvg.toFixed(0),
        diffusePercent,
        directPercent,
      };
    });

    // Calculate annual percentages
    const diffusePercent =
      annualTotalAvg > 0
        ? ((annualDiffuseAvg / annualTotalAvg) * 100).toFixed(1)
        : 0;
    const directPercent =
      annualTotalAvg > 0
        ? ((annualDirectAvg / annualTotalAvg) * 100).toFixed(1)
        : 0;

    // For the annual total, we multiply by 365 to get the total annual radiation
    const annualTotal = annualTotalAvg * 365;
    const annualDirectTotal = annualDirectAvg * 365;
    const annualDiffuseTotal = annualDiffuseAvg * 365;

    console.log(
      `Annual Total Radiation: ${annualTotal.toFixed(0)} Wh/m² ` +
        `(Direct: ${annualDirectTotal.toFixed(
          0
        )}, Diffuse: ${annualDiffuseTotal.toFixed(0)})`
    );

    return {
      annualDirectTotal: annualDirectTotal.toFixed(0),
      annualDiffuseTotal: annualDiffuseTotal.toFixed(0),
      annualTotal: annualTotal.toFixed(0),
      diffusePercent,
      directPercent,
      peakDirect: (peakDirect * 24).toFixed(0), // Scale peak values to daily
      peakDiffuse: (peakDiffuse * 24).toFixed(0),
      peakMonth,
      monthlyTotals,
    };
  };

  // Early return for no data
  if (!solarData || !solarData.data || solarData.data.length === 0) {
    return <div>No solar data available</div>;
  }

  // Check if we're using synthetic data (based on the variable name)
  const isSyntheticData =
    solarData.variable &&
    solarData.variable.name &&
    solarData.variable.name.toLowerCase().includes("synthetic");

  // Get chart data
  const cloudCoverChartData = formatChartData(solarData.data);

  // Format solar radiation data - use radiationData prop if available
  const solarRadiationData = radiationData
    ? formatSolarRadiationData(radiationData.data || radiationData)
    : [];
  const solarStats = radiationData
    ? calculateSolarStats(solarRadiationData)
    : null;

  // Get every month for the X-axis ticks
  const getTickValues = (chartData) => {
    return chartData
      .filter((item) => item.day === 15) // Middle of each month
      .map((item) => item.date.split(" ")[0]); // Only month name
  };

  const cloudCoverTickValues = getTickValues(cloudCoverChartData);
  const cloudCoverStats = calculateStats(cloudCoverChartData);

  // Only render the solar radiation section if we have radiation data
  // Modified to check for radiationData in different formats
  const hasRadiationData =
    radiationData &&
    ((radiationData.data && radiationData.data.length > 0) ||
      (Array.isArray(radiationData) && radiationData.length > 0));

  return (
    <div className="solar-charts">
      <div className="chart-section">
        <h3>Cloud Cover {isSyntheticData && "(Estimated)"}</h3>

        {/* Bar Chart for Monthly Cloud Cover */}
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={monthlyCloudCoverData}
              margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                label={{
                  value: "Month",
                  position: "insideBottomRight",
                  offset: -10,
                }}
              />
              <YAxis
                domain={[0, 100]}
                label={{
                  value: "Cloud Cover (%)",
                  angle: -90,
                  position: "insideLeft",
                }}
              />
              <Tooltip
                formatter={(value, name) => {
                  if (name === "mean") return [`${value}%`, "Mean Cloud Cover"];
                  if (name === "min") return [`${value}%`, "Min Cloud Cover"];
                  if (name === "max") return [`${value}%`, "Max Cloud Cover"];
                  return [value, name];
                }}
                labelFormatter={(label) => `Month: ${label}`}
              />
              <Legend />
              <ReferenceLine
                y={30}
                stroke="#4caf50"
                strokeDasharray="3 3"
                label={{
                  value: "Clear Sky Threshold",
                  position: "insideBottomRight",
                  fill: "#4caf50",
                  fontSize: 12,
                }}
              />
              <ReferenceLine
                y={70}
                stroke="#f44336"
                strokeDasharray="3 3"
                label={{
                  value: "Overcast Threshold",
                  position: "insideTopRight",
                  fill: "#f44336",
                  fontSize: 12,
                }}
              />
              <Bar
                dataKey="mean"
                name="Mean Cloud Cover"
                fill={(data) => getCloudCoverColor(data.mean)}
              />
              <Bar dataKey="min" name="Min Cloud Cover" fill="#82ca9d" />
              <Bar dataKey="max" name="Max Cloud Cover" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Daily Cloud Cover Variation */}
        <div className="chart-container">
          <h4>Daily Cloud Cover Variation</h4>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart
              data={cloudCoverChartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                ticks={cloudCoverTickValues}
                label={{
                  value: "Month",
                  position: "insideBottomRight",
                  offset: -10,
                }}
              />
              <YAxis
                domain={[0, 100]}
                label={{
                  value: "Cloud Cover (%)",
                  angle: -90,
                  position: "insideLeft",
                }}
              />
              <Tooltip
                formatter={(value) => [`${value}%`, "Cloud Cover"]}
                labelFormatter={(label) => `Date: ${label}`}
              />
              <Legend />
              <ReferenceLine
                y={30}
                stroke="#4caf50"
                strokeDasharray="3 3"
                label={{
                  value: "Clear Sky",
                  position: "insideBottomRight",
                  fill: "#4caf50",
                  fontSize: 12,
                }}
              />
              <ReferenceLine
                y={70}
                stroke="#f44336"
                strokeDasharray="3 3"
                label={{
                  value: "Overcast",
                  position: "insideTopRight",
                  fill: "#f44336",
                  fontSize: 12,
                }}
              />
              <Area
                type="monotone"
                dataKey="mean"
                fill="#8884d8"
                stroke="#8884d8"
                fillOpacity={0.3}
              />
              <Line
                type="monotone"
                dataKey="mean"
                stroke="#8884d8"
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-info">
          <div className="cloud-cover-stats">
            <div className="cloud-stat-item">
              <span className="cloud-stat-label">Annual Mean Cloud Cover</span>
              <span className="cloud-stat-value">
                {cloudCoverStats?.annualMean}%
              </span>
            </div>
            <div className="cloud-stat-item">
              <span className="cloud-stat-label">Clear Days</span>
              <span className="cloud-stat-value">
                {cloudCoverStats?.clearDays} days
              </span>
              <span className="cloud-stat-percent">
                (
                {cloudCoverStats?.clearDays && cloudCoverChartData.length
                  ? (
                      (cloudCoverStats.clearDays / cloudCoverChartData.length) *
                      100
                    ).toFixed(1)
                  : "0.0"}
                %)
              </span>
            </div>
            <div className="cloud-stat-item">
              <span className="cloud-stat-label">Partly Cloudy Days</span>
              <span className="cloud-stat-value">
                {cloudCoverStats?.partlyCloudyDays} days
              </span>
              <span className="cloud-stat-percent">
                (
                {cloudCoverStats?.partlyCloudyDays && cloudCoverChartData.length
                  ? (
                      (cloudCoverStats.partlyCloudyDays /
                        cloudCoverChartData.length) *
                      100
                    ).toFixed(1)
                  : "0.0"}
                %)
              </span>
            </div>
            <div className="cloud-stat-item">
              <span className="cloud-stat-label">Overcast Days</span>
              <span className="cloud-stat-value">
                {cloudCoverStats?.cloudyDays} days
              </span>
              <span className="cloud-stat-percent">
                (
                {cloudCoverStats?.cloudyDays && cloudCoverChartData.length
                  ? (
                      (cloudCoverStats.cloudyDays /
                        cloudCoverChartData.length) *
                      100
                    ).toFixed(1)
                  : "0.0"}
                %)
              </span>
            </div>
            <div className="cloud-stat-item">
              <span className="cloud-stat-label">Clearest Month</span>
              <span className="cloud-stat-value">
                {cloudCoverStats?.lowestMonth?.name} (
                {cloudCoverStats?.lowestMonth?.mean
                  ? parseFloat(cloudCoverStats.lowestMonth.mean).toFixed(1)
                  : "0.0"}
                %)
              </span>
            </div>
            <div className="cloud-stat-item">
              <span className="cloud-stat-label">Cloudiest Month</span>
              <span className="cloud-stat-value">
                {cloudCoverStats?.highestMonth?.name} (
                {cloudCoverStats?.highestMonth?.mean
                  ? parseFloat(cloudCoverStats.highestMonth.mean).toFixed(1)
                  : "0.0"}
                %)
              </span>
            </div>
            <div className="cloud-stat-item">
              <span className="cloud-stat-label">Solar Potential</span>
              <span className="cloud-stat-value">
                {calculateSolarPotential(cloudCoverStats?.annualMean)}%
              </span>
            </div>
          </div>

          <p className="data-note">
            {isSyntheticData
              ? "Note: Cloud cover data is estimated based on humidity patterns as direct measurements were not available."
              : "Cloud cover data represents the percentage of sky covered by clouds, affecting solar radiation and daylight availability."}
          </p>
        </div>
      </div>

      {/* Only render the solar radiation section if we have radiation data */}
      {hasRadiationData && solarStats && (
        <div className="chart-section">
          <h3>Solar Radiation Analysis</h3>
          <p className="chart-description">
            Analysis of direct normal and diffuse horizontal radiation
            components throughout the year.
          </p>

          {/* Summary section */}
          <div
            className="solar-summary"
            style={{
              backgroundColor: "#f8f9fa",
              padding: "15px",
              borderRadius: "8px",
              marginBottom: "20px",
            }}
          >
            <h4>Solar Radiation Summary</h4>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
              }}
            >
              <div style={{ flex: "1 1 200px", margin: "10px" }}>
                <p>
                  <strong>Total Annual Radiation:</strong>{" "}
                  {solarStats?.annualTotal || 0} Wh/m²
                </p>
                <p>
                  <strong>Direct Normal Component:</strong>{" "}
                  {solarStats?.annualDirectTotal || 0} Wh/m² (
                  {solarStats?.directPercent || 0}%)
                </p>
                <p>
                  <strong>Diffuse Horizontal Component:</strong>{" "}
                  {solarStats?.annualDiffuseTotal || 0} Wh/m² (
                  {solarStats?.diffusePercent || 0}%)
                </p>
              </div>
              <div style={{ flex: "1 1 200px", margin: "10px" }}>
                <p>
                  <strong>Peak Month:</strong> {solarStats?.peakMonth || "N/A"}
                </p>
                <p>
                  <strong>Peak Direct Radiation:</strong>{" "}
                  {solarStats?.peakDirect || 0} Wh/m²
                </p>
                <p>
                  <strong>Peak Diffuse Radiation:</strong>{" "}
                  {solarStats?.peakDiffuse || 0} Wh/m²
                </p>
              </div>
            </div>
          </div>

          {/* Bar Chart for Monthly Solar Radiation Distribution */}
          <div className="chart-container">
            <h4>Monthly Solar Radiation Distribution</h4>
            <ResponsiveContainer width="100%" height={500}>
              <BarChart
                data={solarStats?.monthlyTotals || []}
                margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  label={{
                    value: "Month",
                    position: "insideBottomRight",
                    offset: -10,
                  }}
                />
                <YAxis
                  label={{
                    value: "Solar Radiation (Wh/m²)",
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === "direct")
                      return [`${value} Wh/m²`, "Direct Normal Radiation"];
                    if (name === "diffuse")
                      return [`${value} Wh/m²`, "Diffuse Horizontal Radiation"];
                    return [value, name];
                  }}
                  labelFormatter={(label) => `Month: ${label}`}
                />
                <Legend />
                <Bar
                  dataKey="direct"
                  name="Direct Normal Radiation"
                  stackId="a"
                  fill="#ff7300"
                />
                <Bar
                  dataKey="diffuse"
                  name="Diffuse Horizontal Radiation"
                  stackId="a"
                  fill="#1e88e5"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Pie Chart for Annual Distribution */}
          <div className="chart-container">
            <h4>Annual Solar Radiation Distribution</h4>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ width: "45%", height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        {
                          name: "Direct Normal",
                          value: parseFloat(solarStats?.directPercent || 0),
                          fill: "#ff7300",
                        },
                        {
                          name: "Diffuse Horizontal",
                          value: parseFloat(solarStats?.diffusePercent || 0),
                          fill: "#1e88e5",
                        },
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                      label={({
                        cx,
                        cy,
                        midAngle,
                        innerRadius,
                        outerRadius,
                        percent,
                        name,
                      }) => {
                        const radius =
                          innerRadius + (outerRadius - innerRadius) * 1.4;
                        const x =
                          cx + radius * Math.cos(-midAngle * (Math.PI / 180));
                        const y =
                          cy + radius * Math.sin(-midAngle * (Math.PI / 180));
                        return (
                          <text
                            x={x}
                            y={y}
                            fill={
                              name === "Direct Normal" ? "#ff7300" : "#1e88e5"
                            }
                            textAnchor={x > cx ? "start" : "end"}
                            dominantBaseline="central"
                          >
                            {name} ({percent * 100}%)
                          </text>
                        );
                      }}
                    />
                    <Tooltip
                      formatter={(value) => [`${value}%`, "Percentage"]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div
                style={{
                  width: "45%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <div className="solar-stat-item">
                  <span className="solar-stat-label">
                    Total Annual Radiation
                  </span>
                  <span className="solar-stat-value">
                    {solarStats?.annualTotal || 0} Wh/m²
                  </span>
                </div>
                <div className="solar-stat-item">
                  <span className="solar-stat-label">Direct Normal</span>
                  <span className="solar-stat-value">
                    {solarStats?.annualDirectTotal || 0} Wh/m²
                  </span>
                  <span className="solar-stat-percent">
                    ({solarStats?.directPercent || 0}%)
                  </span>
                </div>
                <div className="solar-stat-item">
                  <span className="solar-stat-label">Diffuse Horizontal</span>
                  <span className="solar-stat-value">
                    {solarStats?.annualDiffuseTotal || 0} Wh/m²
                  </span>
                  <span className="solar-stat-percent">
                    ({solarStats?.diffusePercent || 0}%)
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="chart-info">
            <p>
              <strong>Solar Radiation Components:</strong> Solar radiation
              reaching the Earth's surface consists of two main components:
              direct normal radiation (beam radiation that comes directly from
              the sun) and diffuse horizontal radiation (scattered by the
              atmosphere and clouds).
            </p>

            {solarStats && (
              <>
                <p>
                  <strong>Annual Distribution:</strong> This location receives
                  approximately {solarStats.annualTotal} Wh/m² of total solar
                  radiation annually, with {solarStats.directPercent}% as direct
                  normal radiation and {solarStats.diffusePercent}% as diffuse
                  horizontal radiation.
                </p>

                <p>
                  <strong>Peak Conditions:</strong> The highest direct normal
                  radiation occurs in {solarStats.peakMonth} with peak values
                  reaching {solarStats.peakDirect} Wh/m². The diffuse component
                  peaks at {solarStats.peakDiffuse} Wh/m².
                </p>

                <p>
                  <strong>Design Implications:</strong> The ratio of direct to
                  diffuse radiation has important implications for solar
                  technology design. Locations with high direct radiation
                  benefit most from concentrating solar technologies, while
                  areas with significant diffuse radiation may be better suited
                  for photovoltaic panels that can utilize both components.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Add a placeholder message if no radiation data is available */}
      {!hasRadiationData && (
        <div className="chart-section">
          <h3>Solar Radiation Analysis</h3>
          <p className="chart-description">
            No solar radiation data is available for this location.
          </p>
        </div>
      )}
    </div>
  );
};

export default SolarExposureCharts;
